import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, MapPin, Mail, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { toast } = useToast();
  const onSubscribe = async (e) => {
    e.preventDefault();
    const email = new FormData(e.target).get('email');
    if (!email) return;
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast({ title: 'You’re on the list', description: `We’ve saved ${email}. You’ll hear from us once our newsletter goes live.` });
      e.target.reset();
    } catch (err) {
      toast({ title: 'Could not save your email', description: 'Please try again shortly.' });
    }
  };
  return (
    <footer className="bg-slate-900 text-slate-300 mt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-display font-extrabold text-white text-lg">Cheapora</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 -mt-0.5">Operated by Waan Travel Pte. Ltd.</div>
              </div>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed max-w-md">
              Cheapora helps you compare and book flights, hotels and cars across trusted travel
              providers — with local Singapore support. Cheapora is operated by Waan Travel Pte. Ltd.,
              a Singapore-registered travel company.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-teal-400" /><span>1 Soon Lee Street, #03-37 Pioneer Centre, Singapore 627605</span></div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-teal-400" /><span>admin@waantravel.com</span></div>
              <div className="flex items-center gap-2 text-slate-400"><span className="w-4 inline-block" />www.cheapora.com</div>
            </div>
            <div className="mt-4 text-xs text-slate-500">
              Cheapora is operated by Waan Travel Pte. Ltd.<br/>
              UEN 202418948C · Licensed Singapore Travel Agent — STB Licence TA03795
            </div>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/flights" className="hover:text-white">Cheap Flights</Link></li>
              <li><Link to="/hotels" className="hover:text-white">Hotels</Link></li>
              <li><Link to="/cars" className="hover:text-white">Car Rentals</Link></li>
              <li><Link to="/deals" className="hover:text-white">Holiday Deals</Link></li>
              <li><Link to="/destinations" className="hover:text-white">Destinations</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white">About Cheapora</Link></li>
              <li><Link to="/blog" className="hover:text-white">Travel Tips</Link></li>
              <li><Link to="/about" className="hover:text-white">Contact Us</Link></li>
              <li><Link to="/affiliate-disclosure" className="hover:text-white">Affiliate Disclosure</Link></li>
              <li><Link to="/price-disclaimer" className="hover:text-white">Price & Availability</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">Notify me</h4>
            <p className="text-sm text-slate-400 mb-3">Leave your email and we’ll let you know when our newsletter goes live.</p>
            <form onSubmit={onSubscribe} className="flex gap-2">
              <Input name="email" required type="email" placeholder="you@email.com" className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Notify me</Button>
            </form>
            <div className="flex items-center gap-3 mt-5">
              <span aria-label="Facebook (coming soon)" title="Coming soon" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-500 opacity-60 cursor-not-allowed"><Facebook className="w-4 h-4" /></span>
              <span aria-label="Instagram (coming soon)" title="Coming soon" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-500 opacity-60 cursor-not-allowed"><Instagram className="w-4 h-4" /></span>
              <span aria-label="Twitter (coming soon)" title="Coming soon" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-500 opacity-60 cursor-not-allowed"><Twitter className="w-4 h-4" /></span>
              <span aria-label="LinkedIn (coming soon)" title="Coming soon" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-500 opacity-60 cursor-not-allowed"><Linkedin className="w-4 h-4" /></span>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-500">
          <p>© 2026 Waan Travel Pte. Ltd. All rights reserved. Cheapora is operated by Waan Travel Pte. Ltd. · UEN 202418948C · STB Licence TA03795 · www.cheapora.com</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/cookies" className="hover:text-white">Cookie Policy</Link>
            <Link to="/provider-disclaimer" className="hover:text-white">Provider Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
